import { unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { roleHistory, registerRoutes, isRoleDeepLink, resolveForRole, rememberRole } from "./lib/roleHistory";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantProvider } from "./context/TenantContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AcknowledgementGate from "./components/AcknowledgementGate";
import GlobalBackground from "./components/GlobalBackground";
import Register from "./pages/Register";
import LandingPage from "./pages/public/LandingPage";
import PlatformLogin from "./pages/public/PlatformLogin";
import VyomosMarketingShell, { VyomosMarketingLayout } from "./pages/public/vyomos/VyomosMarketingShell";
import VyomosLogin from "./pages/public/vyomos/VyomosLogin";
import GoogleContinue from "./pages/GoogleContinue";
import { useTenant } from "./context/TenantContext";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/admin/Dashboard";
import SuperAdminLayout from "./pages/super-admin/SuperAdminLayout";
import SuperAdminOverview from "./pages/super-admin/SuperAdminOverview";
import SuperAdminTenants from "./pages/super-admin/SuperAdminTenants";
import SuperAdminTenantDetail from "./pages/super-admin/SuperAdminTenantDetail";
import SuperAdminUsers from "./pages/super-admin/SuperAdminUsers";
import SuperAdminTenantUsers from "./pages/super-admin/SuperAdminTenantUsers";
import SuperAdminPlans from "./pages/super-admin/SuperAdminPlans";
import SuperAdminPlanChangeRequests from "./pages/super-admin/SuperAdminPlanChangeRequests";
import SuperAdminPlansCompare from "./pages/super-admin/SuperAdminPlansCompare";
import SuperAdminFeatures from "./pages/super-admin/SuperAdminFeatures";
import SuperAdminAuditLog from "./pages/super-admin/SuperAdminAuditLog";
import SuperAdminMaintenance from "./pages/super-admin/SuperAdminMaintenance";
import SuperAdminSettings from "./pages/super-admin/SuperAdminSettings";
import SuperAdminWebsite from "./pages/super-admin/SuperAdminWebsite";
import SuperAdminCronLedger from "./pages/super-admin/SuperAdminCronLedger";
import SuperAdminBillingEmails from "./pages/super-admin/SuperAdminBillingEmails";
import CoachDashboard from "./pages/coach/Dashboard";
import ParentDashboard from "./pages/parent/Dashboard";
import ParentGames from "./pages/parent/Games";
import StaffDashboard from "./pages/staff/Dashboard";
import Programs from "./pages/admin/Programs";
import Classes from "./pages/admin/Classes";
import Coaches from "./pages/admin/Coaches";
import CoachDetail from "./pages/admin/CoachDetail";
import Players from "./pages/admin/Players";
import PlayerDetail from "./pages/admin/PlayerDetail";
import Parents from "./pages/admin/Parents";
import ParentDetail from "./pages/admin/ParentDetail";
import CustomerDetail from "./pages/admin/CustomerDetail";
import AllBookings from "./pages/admin/AllBookings";
import Enrollments from "./pages/admin/Enrollments";
import EnrollmentDetail from "./pages/admin/EnrollmentDetail";
import TrialRequests from "./pages/admin/TrialRequests";
import CoachBilling from "./pages/admin/CoachBilling";
import StaffManagement from "./pages/admin/StaffManagement";
import StaffBilling from "./pages/admin/StaffBilling";
import EnrollmentRequests from "./pages/admin/EnrollmentRequests";
import BusinessHours from "./pages/admin/BusinessHours";
import ShiftScheduling from "./pages/admin/ShiftScheduling";
import Attendance from "./pages/coach/Attendance";
import CoachProfile from "./pages/coach/Profile";
import TimeEntries from "./pages/coach/TimeEntries";
import StaffProfile from "./pages/staff/Profile";
import StaffTimeEntries from "./pages/staff/TimeEntries";
import StaffSchedule from "./pages/staff/Schedule";
import StaffShiftSwaps from "./pages/staff/ShiftSwaps";
import AdminShiftSwaps from "./pages/admin/ShiftSwaps";
import ParentBilling from "./pages/parent/Billing";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import LaneBooking from "./pages/customer/LaneBooking";
import CustomerBilling from "./pages/customer/Billing";
import LaneManagement from "./pages/admin/LaneManagement";
import LaneMaintenance from "./pages/admin/LaneMaintenance";
import LaneSettings from "./pages/admin/LaneSettings";
import EquipmentManagement from "./pages/admin/EquipmentManagement";
import ParentOnboarding from "./pages/admin/ParentOnboarding";
import AdminTournaments from "./pages/admin/Tournaments";
import TournamentDetail from "./pages/admin/TournamentDetail";
import Discounts from "./pages/admin/Discounts";
import BillingOverview from "./pages/admin/BillingManagementNew";
import AgedReceivablesPage from "./pages/admin/AgedReceivablesPage";
import RefundsManagement from "./pages/admin/RefundsManagement";
import InvoiceManagement from "./pages/admin/InvoiceManagement";
import ReconciliationAuditLog from "./pages/admin/ReconciliationAuditLog";
import InvoicePrint from "./pages/admin/InvoicePrint";
import AdminSanity from "./pages/admin/AdminSanity";
import Transactions from "./pages/admin/Transactions";
import CreditLedgerPage from "./pages/admin/billing/CreditLedgerPage";
import MovementRequests from "./pages/admin/MovementRequests";
import ClassScheduleRequests from "./pages/admin/ClassScheduleRequests";
import UniformOrder from "./pages/admin/UniformOrder";
import DuplicateManagement from "./pages/admin/DuplicateManagement";
import CoachTournaments from "./pages/coach/Tournaments";
import CoachMovementRequests from "./pages/coach/MovementRequests";
import ParentTournaments from "./pages/parent/Tournaments";
import ParentTournamentDetail from "./pages/parent/TournamentDetail";
import ParentChildren from "./pages/parent/Children";
import ParentEnrollments from "./pages/parent/Enrollments";
import ParentPrograms from "./pages/parent/Programs";
import ParentAttendance from "./pages/parent/Attendance";
import AdminAttendance from "./pages/admin/Attendance";
import FeatureSettings from "./pages/admin/FeatureSettings";
import HomepageEditor from "./pages/admin/HomepageEditor";
import CronJobsSettings from "./pages/admin/CronJobsSettings";
import AuditLogs from "./pages/admin/AuditLogs";
import StaffTournaments from "./pages/staff/Tournaments";
import ContactUs from "./pages/shared/ContactUs";
import EmailTemplates from "./pages/admin/EmailTemplates";
import EmailCampaigns from "./pages/admin/EmailCampaigns";
import EmailCampaignDetail from "./pages/admin/EmailCampaignDetail";
import EmailSuppressions from "./pages/admin/EmailSuppressions";
import EmailSettings from "./pages/admin/EmailSettings";
import ErrorLogs from "./pages/admin/ErrorLogs";
import WithdrawalRequests from "./pages/admin/WithdrawalRequests";
import UserManagement from "./pages/admin/UserManagement";
import LegalDocuments from "./pages/admin/LegalDocuments";
import LegalDocumentView from "./pages/public/LegalDocumentView";
import ResetPassword from "./pages/public/ResetPassword";
import Unsubscribe from "./pages/Unsubscribe";
import PublicPrograms from "./pages/public/PublicPrograms";
import PublicLaneBooking from "./pages/public/PublicLaneBooking";
import PublicCampRegister from "./pages/public/PublicCampRegister";
import PublicCampRegisterSuccess from "./pages/public/PublicCampRegisterSuccess";
import BackupRestore from "./pages/admin/BackupRestore";
import PlayerDashboard from "./pages/player/PlayerDashboard";
import PlayerPrograms from "./pages/player/PlayerPrograms";
import StripeSettings from "./pages/admin/StripeSettings";
import LocationSettings from "./pages/admin/LocationSettings";
import PushNotificationSettings from "./pages/admin/PushNotificationSettings";
import WhatsAppInbox from "./pages/admin/WhatsAppInbox";
import WhatsAppSettings from "./pages/admin/WhatsAppSettings";
import WhatsAppTemplates from "./pages/admin/WhatsAppTemplates";
import TeamChats from "./pages/TeamChats";
import MyFamily from "./pages/MyFamily";
import ParentSetup from "./pages/parent/Onboarding";
import SmallGroupManagement from "./pages/admin/SmallGroupManagement";
import ParentSmallGroup from "./pages/parent/SmallGroupSessions";
import ParentCamps from "./pages/parent/Camps";
import AcademyDocuments from "./pages/common/AcademyDocuments";
import UniformCampaignsView from "./pages/common/UniformCampaignsView";
import UniformVerification from "./pages/parent/UniformVerification";
import ParentUniformOrders from "./pages/parent/UniformOrders";
import DocumentCampaigns from "./pages/admin/DocumentCampaigns";
import DocumentUpload from "./pages/parent/DocumentUpload";
import OurPlayers from "./pages/public/OurPlayers";
import HeroImagesManagement from "./pages/admin/HeroImagesManagement";
import EventsManagement from "./pages/admin/EventsManagement";
import PublicEventRsvp from "./pages/public/PublicEventRsvp";
import PublicPay from "./pages/public/PublicPay";
import PublicSmallGroupRegister from "./pages/public/PublicSmallGroupRegister";
import PublicBooking from "./pages/public/PublicBooking";
import ParentEvents from "./pages/parent/Events";
import ParentNotifications from "./pages/parent/Notifications";
import BlogManagement from "./pages/admin/BlogManagement";
import SplashManagement from "./pages/admin/SplashManagement";
import CampManagement from "./pages/admin/CampManagement";
import { BlogList, BlogPost } from "./pages/public/Blog";
import DeskDisplay from "./pages/public/DeskDisplay";
import { useState, useEffect } from "react";
import api from "./services/api";
import { FeatureProvider, useFeatures } from "./context/FeatureContext";import { useFeature } from "./hooks/useFeature";
import { DashboardLayout } from "./components/DashboardLayout";
import { ViewModeProvider } from "./context/ViewModeContext";
import { useViewMode } from "./context/ViewModeContext";
import { ParentModeBanner } from "./components/ViewModeSwitcher";
import UpgradeModal from "./components/UpgradeModal";
import { PlanLockedCard } from "./components/PlanLockedCard";
import { planFeatureForPath } from "./components/layout/navConfig";
import { CookieNotice } from "./components/CookieNotice";
import "./App.css";

// iter372 — Host-authoritative routing guard. The Vyomos marketing /
// global-login shells must ONLY ever render on real platform hosts
// (www.vyomos.io / vyomos.io / my.vyomos.io) or under the explicit QA
// preview override. On a tenant subdomain or white-label custom domain
// we must NEVER render the platform shell even if `branding.route_intent`
// says "vyomos_marketing" — a stale/poisoned branding cache (e.g. left
// over from a DNS/redirect outage) was hijacking tenant roots and showing
// the Vyomos homepage instead of the academy landing.
const _ROUTING_PLATFORM_HOSTS = new Set(
  (process.env.REACT_APP_PLATFORM_HOSTS || 'www.vyomos.io,vyomos.io,my.vyomos.io,localhost,127.0.0.1')
    .split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
);
const isPlatformRoutingHost = () => {
  if (typeof window === 'undefined') return false;
  const h = (window.location.hostname || '').toLowerCase();
  if (_ROUTING_PLATFORM_HOSTS.has(h)) return true;
  try {
    // QA / stakeholder override to exercise the marketing shell on any
    // preview URL (never sent to the API; mirrors TenantContext).
    if (new URLSearchParams(window.location.search).get('_vyomos_intent') === 'marketing') return true;
  } catch (e) { /* no-op */ }
  return false;
};

// iter308j — Root route gate. Platform hosts (my.vyomos.io etc.)
// see a generic Vyomos login screen. Tenant hosts see their
// academy's full marketing/landing page.
const RootRoute = () => {
  const { branding, loading } = useTenant();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }
  // On any non-platform host (tenant subdomain / custom domain) always
  // render the academy landing — the platform shell can never apply here.
  if (!isPlatformRoutingHost()) return <LandingPage />;
  // iter318 wave 12 — Route by intent, not just is_platform:
  //   * vyomos_marketing (www.vyomos.io / vyomos.io) → marketing shell
  //   * global_login (my.vyomos.io) → PlatformLogin (auto-redirects
  //     the user to their tenant subdomain after successful auth)
  //   * tenant_app (default) → LandingPage (per-tenant Sign-in modal
  //     with the academy's branding)
  const intent = branding?.route_intent;
  if (intent === "vyomos_marketing") return <VyomosMarketingShell />;
  if (intent === "global_login" || branding?.is_platform) return <PlatformLogin />;
  return <LandingPage />;
};

// iter318 wave 16 — /login gate. Marketing hosts render the shell's
// "Find my academy" subdomain helper; every other host keeps the legacy
// tenant/platform login modal via the ?tab=login query param.
const LoginRoute = () => {
  const { branding, loading } = useTenant();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }
  if (branding?.route_intent === "vyomos_marketing" && isPlatformRoutingHost()) {
    // Render VyomosLogin directly wrapped in the marketing layout.
    // We sidestep the shell's descendant <Routes> — React Router v6's
    // strict splat-matching rules make nested-Routes matching for /login
    // unreliable, and this route only ever renders one page anyway.
    return (
      <VyomosMarketingLayout>
        <VyomosLogin />
      </VyomosMarketingLayout>
    );
  }
  return <Navigate to="/?tab=login" replace />;
};

// iter318 wave 13 — Catch-all that keeps marketing sub-paths (/pricing,
// /contact, /about, /product…) mounted on the VyomOS marketing shell
// while still redirecting stray URLs to root on tenant hosts.
const CatchAllRoute = () => {
  const { branding, loading } = useTenant();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }
  if (branding?.route_intent === "vyomos_marketing" && isPlatformRoutingHost()) {
    return <VyomosMarketingShell />;
  }
  return <RoleDeepLinkFallback />;
};

// A role-less deep link (e.g. /tournaments/<id>) opened while signed out:
// remember it and send the visitor to sign in; after login it resolves to
// that user's own version of the page (or their dashboard).
const RoleDeepLinkFallback = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen bg-slate-50" data-testid="deep-link-resolving" />;
  }
  if (isRoleDeepLink(location.pathname)) {
    if (!user) {
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      return <Navigate to="/?tab=login" replace />;
    }
    // Location was read before the role was known (cross-host handoff).
    const target = resolveForRole(location.pathname, user.role);
    if (target !== location.pathname) return <Navigate to={target + location.search} replace />;
  }
  return <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const { isParentMode } = useViewMode();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    // Save the intended URL to localStorage for redirect after login
    if (location.pathname !== '/' && location.pathname !== '/login') {
      localStorage.setItem('redirectAfterLogin', location.pathname);
    }
    // No session → drop the remembered role so "/" resolves to the public landing again.
    rememberRole(null);
    return <Navigate to="/?tab=login" replace />;
  }

  // Wave 24u.3 — When an admin/coach/staff is in Parent view mode
  // (opted in via the top-bar switcher), route them as if their role
  // were `parent`. This hides admin/coach/staff shells entirely (q6-a)
  // and lets them use every parent-scope feature on their own kids.
  const effectiveRole = isParentMode ? 'parent' : user.role;

  if (allowedRoles && !allowedRoles.includes(effectiveRole)) {
    // Redirect to appropriate dashboard based on effective role.
    // Super-admins must ALWAYS land on the platform console and never
    // render a tenant admin view (role collision fix).
    if (effectiveRole === "super_admin") return <Navigate to="/super-admin" replace />;
    if (effectiveRole === "admin") return <Navigate to="/admin" replace />;
    if (effectiveRole === "coach") return <Navigate to="/coach" replace />;
    if (effectiveRole === "parent") return <Navigate to="/parent" replace />;
    if (effectiveRole === "staff") return <Navigate to="/staff" replace />;
    if (effectiveRole === "customer") return <Navigate to="/book" replace />;
    if (effectiveRole === "player") return <Navigate to="/player" replace />;
  }
  
  return children;
};

// Prevent a bookmarked or manually entered URL from rendering a tenant
// feature surface that the sidebar has correctly hidden.
const PlanFeatureRoute = ({ featureKey, children }) => {
  const { user } = useAuth();
  const { enabled } = useFeature(featureKey);
  if (!enabled) {
    return (
      <DashboardLayout>
        <PlanLockedCard isAdmin={user?.role === 'admin'} />
      </DashboardLayout>
    );
  }
  return children;
};

// Parent onboarding check wrapper
const ParentOnboardingCheck = ({ children }) => {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (user?.role !== 'parent') {
        setChecking(false);
        return;
      }

      try {
        const response = await api.get('/parent/onboarding-status');
        setNeedsOnboarding(response.data.onboarding_required);
      } catch (error) {
        console.error('Error checking onboarding:', error);
        setNeedsOnboarding(false);
      } finally {
        setChecking(false);
      }
    };

    checkOnboarding();
  }, [user]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (needsOnboarding) {
    return <Navigate to="/parent/setup" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isParentMode } = useViewMode();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    // Wave 24u.3 — parent view-mode users always land on /parent.
    const effectiveRole = isParentMode ? 'parent' : user.role;
    if (effectiveRole === "super_admin") return <Navigate to="/super-admin" replace />;
    if (effectiveRole === "admin") return <Navigate to="/admin" replace />;
    if (effectiveRole === "coach") return <Navigate to="/coach" replace />;
    if (effectiveRole === "parent") return <Navigate to="/parent" replace />;
    if (effectiveRole === "staff") return <Navigate to="/staff" replace />;
    if (effectiveRole === "customer") return <Navigate to="/book" replace />;
  }
  
  return children;
};

// Maintenance mode wrapper - checks if app is under maintenance
const MaintenanceWrapper = ({ children }) => {
  const { user } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await api.get('/maintenance-status');
        setMaintenanceMode(response.data.maintenance_mode);
        setMaintenanceMessage(response.data.maintenance_message);
      } catch (error) {
        // If we can't reach the API, don't block access
        setMaintenanceMode(false);
      } finally {
        setChecking(false);
      }
    };

    checkMaintenance();
    // Check every 30 seconds
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  // Store maintenance status in window for LandingPage to access
  window.__MAINTENANCE_STATUS__ = {
    isActive: maintenanceMode,
    message: maintenanceMessage,
    canBypass: user?.role === 'admin'
  };

  return children;
};

// Route table at module scope so the role-less URL history can learn every
// route pattern before the router mounts.
const routeTree = (
  <>
              {/* iter318 wave 16 — On the VyomOS marketing host, /login mounts
                  the "Find my academy" subdomain helper (rendered by the
                  marketing shell). On every other host we keep the legacy
                  behaviour of redirecting to the tenant/platform login modal.
                  Note: NO trailing "/*" — v6 descendant <Routes> matches
                  against the original pathname when parent path has no splat. */}
              <Route path="/login" element={<LoginRoute />} />
              {/* iter318 wave 17 — Google Auth handoff/callback landing.
                  Works on every tenant subdomain — the backend resolves
                  tenant from the request host. */}
              <Route path="/auth/google-continue" element={<GoogleContinue />} />
              <Route path="/auth/super-admin-continue" element={<GoogleContinue mode="super_admin" />} />
              <Route path="/auth/callback" element={<GoogleContinue />} />
              <Route path="/desk-display/booking/:sessionId" element={<DeskDisplay />} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
          
          {/* Public Legal Document Routes */}
          <Route path="/privacy-policy" element={<LegalDocumentView />} />
          <Route path="/shipping-policy" element={<LegalDocumentView />} />
          <Route path="/terms-of-service" element={<LegalDocumentView />} />
          <Route path="/return-refund-policy" element={<LegalDocumentView />} />
          <Route path="/do-not-sell-info" element={<LegalDocumentView />} />
          
          {/* Public Pages */}
          <Route path="/our-players" element={<OurPlayers />} />
          <Route path="/events/rsvp/:token" element={<PublicEventRsvp />} />
          <Route path="/pay/:token" element={<PublicPay />} />
          <Route path="/book" element={<PublicBooking />} />
          <Route path="/register/small-group/:slotId" element={<ProtectedRoute allowedRoles={["parent", "customer"]}><PublicSmallGroupRegister /></ProtectedRoute>} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:postId" element={<BlogPost />} />
          
          {/* Profile - Available to all logged in users */}
          <Route path="/profile" element={<ProtectedRoute allowedRoles={["admin", "coach", "staff", "parent", "customer", "player"]}><Profile /></ProtectedRoute>} />
          <Route path="/team-chats" element={<ProtectedRoute allowedRoles={["admin", "coach", "staff", "parent"]}><PlanFeatureRoute featureKey="comms.team_chats"><TeamChats /></PlanFeatureRoute></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />

          {/* Super-Admin Routes (iter307du Phase 4) */}
          <Route path="/super-admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminLayout /></ProtectedRoute>}>
            {/* iter309-redesign PR 2: default landing is now Platform Overview. */}
            <Route index element={<Navigate to="/super-admin/overview" replace />} />
            <Route path="overview" element={<SuperAdminOverview />} />
            <Route path="tenants" element={<SuperAdminTenants />} />
            <Route path="tenants/:tenantId" element={<SuperAdminTenantDetail />} />
            {/* Legacy routes still reachable via direct URL / deep links —
                just removed from the primary nav sidebar. Users/Tenant
                Users will merge into Tenant Detail → Users tab in PR 4. */}
            <Route path="users" element={<SuperAdminUsers />} />
            <Route path="tenant-users" element={<SuperAdminTenantUsers />} />
            <Route path="plans" element={<SuperAdminPlans />} />
            <Route path="plan-requests" element={<SuperAdminPlanChangeRequests />} />
            <Route path="plans/compare" element={<SuperAdminPlansCompare />} />
            <Route path="features" element={<SuperAdminFeatures />} />
            <Route path="website" element={<SuperAdminWebsite />} />
            <Route path="maintenance" element={<SuperAdminMaintenance />} />
            <Route path="endpoint-report" element={<Navigate to="/super-admin/maintenance?tab=endpoint-report" replace />} />
            <Route path="settings" element={<SuperAdminSettings />} />
            {/* iter309-redesign PR 6 — legacy standalone ops pages
                redirect into the Maintenance Center tabs. Deep links
                and runbooks still work. */}
            <Route path="cron-ledger" element={<Navigate to="/super-admin/maintenance?tab=cron" replace />} />
            <Route path="billing-emails" element={<Navigate to="/super-admin/maintenance?tab=billing-emails" replace />} />
            <Route path="audit-log" element={<Navigate to="/super-admin/maintenance?tab=audit" replace />} />
          </Route>
          <Route path="/admin/programs" element={<ProtectedRoute allowedRoles={["admin"]}><Programs /></ProtectedRoute>} />
          <Route path="/admin/classes" element={<ProtectedRoute allowedRoles={["admin"]}><Classes /></ProtectedRoute>} />
          <Route path="/admin/coaches" element={<ProtectedRoute allowedRoles={["admin"]}><Coaches /></ProtectedRoute>} />
          <Route path="/admin/coaches/:coachId" element={<ProtectedRoute allowedRoles={["admin"]}><CoachDetail /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute allowedRoles={["admin"]}><CoachBilling /></ProtectedRoute>} />
          <Route path="/admin/staff" element={<ProtectedRoute allowedRoles={["admin"]}><StaffManagement /></ProtectedRoute>} />
          <Route path="/admin/staff-billing" element={<ProtectedRoute allowedRoles={["admin"]}><StaffBilling /></ProtectedRoute>} />
          <Route path="/admin/players" element={<ProtectedRoute allowedRoles={["admin"]}><Players /></ProtectedRoute>} />
          <Route path="/admin/players/:playerId" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><PlayerDetail /></ProtectedRoute>} />
          <Route path="/admin/parents" element={<ProtectedRoute allowedRoles={["admin"]}><Parents /></ProtectedRoute>} />
          <Route path="/admin/customers" element={<ProtectedRoute allowedRoles={["admin"]}><Parents mode="customers" /></ProtectedRoute>} />
          <Route path="/admin/parents/:parentId" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><ParentDetail /></ProtectedRoute>} />
          <Route path="/admin/customers/:customerId" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><CustomerDetail /></ProtectedRoute>} />
          <Route path="/admin/all-bookings" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><AllBookings /></ProtectedRoute>} />
          <Route path="/admin/enrollments" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Enrollments /></ProtectedRoute>} />
          <Route path="/admin/enrollments/:enrollmentId" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EnrollmentDetail /></ProtectedRoute>} />
          <Route path="/admin/trials" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><TrialRequests /></ProtectedRoute>} />
          <Route path="/admin/subscription-requests" element={<Navigate to="/admin/enrollment-requests" replace />} />
          <Route path="/admin/enrollment-requests" element={<ProtectedRoute allowedRoles={["admin"]}><EnrollmentRequests /></ProtectedRoute>} />
          <Route path="/admin/business-hours" element={<ProtectedRoute allowedRoles={["admin"]}><BusinessHours /></ProtectedRoute>} />
          <Route path="/admin/scheduling" element={<ProtectedRoute allowedRoles={["admin"]}><ShiftScheduling /></ProtectedRoute>} />
          <Route path="/admin/shift-swaps" element={<ProtectedRoute allowedRoles={["admin"]}><AdminShiftSwaps /></ProtectedRoute>} />
          <Route path="/admin/lanes" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><LaneManagement /></ProtectedRoute>} />
          <Route path="/admin/lanes/equipment" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EquipmentManagement /></ProtectedRoute>} />
          <Route path="/admin/lanes/maintenance" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><LaneMaintenance /></ProtectedRoute>} />
          <Route path="/admin/lanes/settings" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><LaneSettings /></ProtectedRoute>} />
          <Route path="/admin/onboarding" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><ParentOnboarding /></ProtectedRoute>} />
          <Route path="/admin/tournaments" element={<ProtectedRoute allowedRoles={["admin"]}><AdminTournaments /></ProtectedRoute>} />
          <Route path="/admin/tournaments/:id" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><TournamentDetail /></ProtectedRoute>} />
          <Route path="/admin/discounts" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Discounts /></ProtectedRoute>} />
          <Route path="/admin/billing-management" element={<ProtectedRoute allowedRoles={["admin"]}><BillingOverview /></ProtectedRoute>} />
          <Route path="/admin/aged-receivables" element={<ProtectedRoute allowedRoles={["admin"]}><AgedReceivablesPage /></ProtectedRoute>} />
          <Route path="/admin/refunds" element={<ProtectedRoute allowedRoles={["admin"]}><RefundsManagement /></ProtectedRoute>} />
          <Route path="/admin/invoice-management" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><InvoiceManagement /></ProtectedRoute>} />
          <Route path="/admin/invoices/:invoiceId/print" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><InvoicePrint /></ProtectedRoute>} />
          <Route path="/admin/sanity" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSanity /></ProtectedRoute>} />
          <Route path="/admin/transactions" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Transactions /></ProtectedRoute>} />
          <Route path="/admin/credit-ledger" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><CreditLedgerPage /></ProtectedRoute>} />
          <Route path="/admin/movement-requests" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><MovementRequests /></ProtectedRoute>} />
          <Route path="/admin/schedule-requests" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><ClassScheduleRequests /></ProtectedRoute>} />
          <Route path="/admin/uniform-order" element={<ProtectedRoute allowedRoles={["admin"]}><UniformOrder /></ProtectedRoute>} />
          <Route path="/admin/document-campaigns" element={<ProtectedRoute allowedRoles={["admin"]}><DocumentCampaigns /></ProtectedRoute>} />
          <Route path="/admin/attendance" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAttendance /></ProtectedRoute>} />
          <Route path="/admin/small-group" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><SmallGroupManagement /></ProtectedRoute>} />
          <Route path="/admin/email-templates" element={<ProtectedRoute allowedRoles={["admin"]}><EmailTemplates /></ProtectedRoute>} />
          <Route path="/admin/email-campaigns" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EmailCampaigns /></ProtectedRoute>} />
          <Route path="/admin/email-campaigns/:id" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EmailCampaignDetail /></ProtectedRoute>} />
          <Route path="/admin/email-suppressions" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EmailSuppressions /></ProtectedRoute>} />
          <Route path="/admin/email-settings" element={<ProtectedRoute allowedRoles={["admin"]}><EmailSettings /></ProtectedRoute>} />
          <Route path="/admin/error-logs" element={<ProtectedRoute allowedRoles={["admin"]}><ErrorLogs /></ProtectedRoute>} />
          <Route path="/admin/withdrawal-requests" element={<ProtectedRoute allowedRoles={["admin"]}><WithdrawalRequests /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["admin"]}><UserManagement /></ProtectedRoute>} />
          <Route path="/admin/documents" element={<ProtectedRoute allowedRoles={["admin"]}><LegalDocuments /></ProtectedRoute>} />
          <Route path="/admin/backup" element={<ProtectedRoute allowedRoles={["admin"]}><BackupRestore /></ProtectedRoute>} />
          <Route path="/admin/payment-settings" element={<ProtectedRoute allowedRoles={["admin"]}><StripeSettings /></ProtectedRoute>} />
          <Route path="/admin/reconciliation-audit" element={<ProtectedRoute allowedRoles={["admin"]}><ReconciliationAuditLog /></ProtectedRoute>} />
          <Route path="/admin/settings/stripe" element={<ProtectedRoute allowedRoles={["admin"]}><StripeSettings /></ProtectedRoute>} />
          <Route path="/admin/settings/locations" element={<ProtectedRoute allowedRoles={["admin"]}><LocationSettings /></ProtectedRoute>} />
          <Route path="/admin/settings/features" element={<ProtectedRoute allowedRoles={["admin"]}><FeatureSettings /></ProtectedRoute>} />
          <Route path="/admin/settings/homepage" element={<ProtectedRoute allowedRoles={["admin"]}><HomepageEditor /></ProtectedRoute>} />
          <Route path="/admin/settings/cron-jobs" element={<ProtectedRoute allowedRoles={["admin"]}><CronJobsSettings /></ProtectedRoute>} />
          <Route path="/admin/settings/push-notifications" element={<ProtectedRoute allowedRoles={["admin"]}><PushNotificationSettings /></ProtectedRoute>} />
          <Route path="/admin/whatsapp" element={<ProtectedRoute allowedRoles={["admin"]}><WhatsAppInbox /></ProtectedRoute>} />
          <Route path="/admin/whatsapp/settings" element={<ProtectedRoute allowedRoles={["admin"]}><WhatsAppSettings /></ProtectedRoute>} />
          <Route path="/admin/whatsapp/templates" element={<ProtectedRoute allowedRoles={["admin"]}><WhatsAppTemplates /></ProtectedRoute>} />
          <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={["admin"]}><AuditLogs /></ProtectedRoute>} />
          <Route path="/admin/duplicates" element={<ProtectedRoute allowedRoles={["admin"]}><DuplicateManagement /></ProtectedRoute>} />
          <Route path="/admin/my-family" element={<ProtectedRoute allowedRoles={["admin"]}><MyFamily /></ProtectedRoute>} />
          <Route path="/admin/academy-documents" element={<ProtectedRoute allowedRoles={["admin"]}><AcademyDocuments /></ProtectedRoute>} />
          <Route path="/admin/hero-images" element={<ProtectedRoute allowedRoles={["admin"]}><HeroImagesManagement /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><EventsManagement /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute allowedRoles={["admin"]}><BlogManagement /></ProtectedRoute>} />
          <Route path="/admin/splash" element={<ProtectedRoute allowedRoles={["admin"]}><SplashManagement /></ProtectedRoute>} />
          <Route path="/admin/camps" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><CampManagement /></ProtectedRoute>} />
          
          {/* Staff Routes */}
          <Route path="/staff" element={<ProtectedRoute allowedRoles={["staff"]}><StaffDashboard /></ProtectedRoute>} />
          <Route path="/staff/hours" element={<ProtectedRoute allowedRoles={["staff"]}><StaffTimeEntries /></ProtectedRoute>} />
          <Route path="/staff/schedule" element={<ProtectedRoute allowedRoles={["staff"]}><StaffSchedule /></ProtectedRoute>} />
          <Route path="/staff/shift-swaps" element={<ProtectedRoute allowedRoles={["staff"]}><StaffShiftSwaps /></ProtectedRoute>} />
          <Route path="/staff/profile" element={<ProtectedRoute allowedRoles={["staff"]}><StaffProfile /></ProtectedRoute>} />
          <Route path="/staff/enrollments" element={<ProtectedRoute allowedRoles={["staff"]}><Enrollments /></ProtectedRoute>} />
          <Route path="/staff/trials" element={<ProtectedRoute allowedRoles={["staff"]}><TrialRequests /></ProtectedRoute>} />
          <Route path="/staff/onboarding" element={<ProtectedRoute allowedRoles={["staff"]}><ParentOnboarding /></ProtectedRoute>} />
          <Route path="/staff/tournaments" element={<ProtectedRoute allowedRoles={["staff"]}><StaffTournaments /></ProtectedRoute>} />
          <Route path="/staff/my-family" element={<ProtectedRoute allowedRoles={["staff"]}><MyFamily /></ProtectedRoute>} />
          
          {/* Coach Routes */}
          <Route path="/coach" element={<ProtectedRoute allowedRoles={["coach"]}><CoachDashboard /></ProtectedRoute>} />
          <Route path="/coach/hours" element={<ProtectedRoute allowedRoles={["coach"]}><TimeEntries /></ProtectedRoute>} />
          <Route path="/coach/attendance" element={<ProtectedRoute allowedRoles={["coach"]}><Attendance /></ProtectedRoute>} />
          <Route path="/coach/profile" element={<ProtectedRoute allowedRoles={["coach"]}><CoachProfile /></ProtectedRoute>} />
          <Route path="/coach/tournaments" element={<ProtectedRoute allowedRoles={["coach"]}><CoachTournaments /></ProtectedRoute>} />
          <Route path="/coach/movement-requests" element={<ProtectedRoute allowedRoles={["coach"]}><CoachMovementRequests /></ProtectedRoute>} />
          <Route path="/coach/my-family" element={<ProtectedRoute allowedRoles={["coach"]}><MyFamily /></ProtectedRoute>} />
          <Route path="/coach/academy-documents" element={<ProtectedRoute allowedRoles={["coach"]}><AcademyDocuments /></ProtectedRoute>} />
          <Route path="/coach/uniform-campaigns" element={<ProtectedRoute allowedRoles={["coach"]}><UniformCampaignsView /></ProtectedRoute>} />
          
          {/* Parent Routes */}
          {/* Parent Setup - accessible without onboarding check */}
          <Route path="/parent/setup" element={<ProtectedRoute allowedRoles={["parent"]}><ParentSetup /></ProtectedRoute>} />
          
          {/* Parent Routes - wrapped with onboarding check */}
          <Route path="/parent" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentDashboard /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/children" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentChildren /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/enrollments" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentEnrollments /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/programs" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentPrograms /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/attendance" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentAttendance /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/billing" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentBilling /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/games" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentGames /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/subscriptions" element={<Navigate to="/parent/enrollments" replace />} />
          <Route path="/parent/book-lane" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><LaneBooking /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/small-group" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentSmallGroup /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/camps" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentCamps /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/tournaments/:tournamentId/pay" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentTournaments /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/tournaments/:tournamentId" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentTournamentDetail /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/tournaments" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentTournaments /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/contact" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ContactUs /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/academy-documents" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><AcademyDocuments /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/uniform-verification" element={<ProtectedRoute allowedRoles={["parent", "admin"]}><UniformVerification /></ProtectedRoute>} />
          <Route path="/parent/uniform-orders" element={<ProtectedRoute allowedRoles={["parent"]}><ParentUniformOrders /></ProtectedRoute>} />
          <Route path="/parent/document-upload" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><DocumentUpload /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/events" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentEvents /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/parent/notifications" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><ParentNotifications /></ParentOnboardingCheck></ProtectedRoute>} />
          <Route path="/my-family" element={<ProtectedRoute allowedRoles={["parent"]}><ParentOnboardingCheck><MyFamily /></ParentOnboardingCheck></ProtectedRoute>} />
          
          {/* Customer Routes */}
          <Route path="/book" element={<ProtectedRoute allowedRoles={["customer", "parent"]}><LaneBooking /></ProtectedRoute>} />
          <Route path="/customer/contact" element={<ProtectedRoute allowedRoles={["customer"]}><ContactUs /></ProtectedRoute>} />
          <Route path="/customer/billing" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerBilling /></ProtectedRoute>} />
          
          {/* Player Routes */}
          <Route path="/player" element={<ProtectedRoute allowedRoles={["player"]}><PlayerDashboard /></ProtectedRoute>} />
          <Route path="/player/programs" element={<ProtectedRoute allowedRoles={["player"]}><PlayerPrograms /></ProtectedRoute>} />
          <Route path="/player/book-lane" element={<ProtectedRoute allowedRoles={["player"]}><LaneBooking /></ProtectedRoute>} />
          <Route path="/player/contact" element={<ProtectedRoute allowedRoles={["player"]}><ContactUs /></ProtectedRoute>} />
          <Route path="/player/academy-documents" element={<ProtectedRoute allowedRoles={["player"]}><AcademyDocuments /></ProtectedRoute>} />
          
          {/* Staff Academy Documents */}
          <Route path="/staff/academy-documents" element={<ProtectedRoute allowedRoles={["staff"]}><AcademyDocuments /></ProtectedRoute>} />
          <Route path="/staff/uniform-campaigns" element={<ProtectedRoute allowedRoles={["staff"]}><UniformCampaignsView /></ProtectedRoute>} />
          
          {/* Payment Routes */}
          <Route path="/payments/success" element={<PaymentSuccess />} />
          <Route path="/payments/cancel" element={<PaymentCancel />} />
          
          {/* Public Routes */}
          <Route path="/trial" element={<LandingPage />} />
          <Route path="/programs" element={<PublicPrograms />} />
          <Route path="/book-lane" element={<PublicLaneBooking />} />
          <Route path="/camps/register" element={<PublicCampRegister />} />
          <Route path="/camps/register/success" element={<PublicCampRegisterSuccess />} />
          
          {/* Default redirect - now goes to landing page */}
          <Route path="/" element={<RootRoute />} />
          <Route path="*" element={<CatchAllRoute />} />
  </>
);
registerRoutes(routeTree);
roleHistory.cleanCurrentUrl();

// Pages whose nav entry needs a plan feature the academy lacks never mount (no fetches, no error toasts).
function PlanRouteGate({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const { isPlanFeatureEnabled, planLoaded } = useFeatures();
  const key = planFeatureForPath(location.pathname);
  if (key && user && !planLoaded) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }
  if (key && !isPlanFeatureEnabled(key)) {
    return <DashboardLayout><PlanLockedCard isAdmin={user?.role === 'admin'} /></DashboardLayout>;
  }
  return children;
}

const EXTERNAL_DEVELOPMENT_TEST_FEATURE = "external_development_test";function TenantDevelopmentTestLabel() {  const { enabled, planFeatures } = useFeature(EXTERNAL_DEVELOPMENT_TEST_FEATURE);  if (!enabled || !planFeatures.includes(EXTERNAL_DEVELOPMENT_TEST_FEATURE)) {    return null;  }  return (    <div      role="status"      aria-label="External development test environment"      style={{        position: "fixed",        top: 12,        left: "50%",        transform: "translateX(-50%)",        zIndex: 2147483647,        padding: "8px 12px",        borderRadius: 9999,        background: "#111827",        color: "#ffffff",        fontSize: 12,        fontWeight: 700,      }}    >      Tenant Development Test — Oct 27, 2026    </div>  );}function App() {  return (
    <TenantProvider>
      <AuthProvider>
        <ViewModeProvider>
        <FeatureProvider>
        <TenantDevelopmentTestLabel />
          <ImpersonationBanner />
          <UpgradeModal />
          <ParentModeBanner />
        <HistoryRouter history={roleHistory}>
          <GlobalBackground />
          <MaintenanceWrapper>
            <AcknowledgementGate>
            <PlanRouteGate>
            <Routes>
              {routeTree.props.children}
        </Routes>
            </PlanRouteGate>
        </AcknowledgementGate>
        </MaintenanceWrapper>
        <Toaster position="top-right" richColors />
        <CookieNotice />
      </HistoryRouter>
      </FeatureProvider>
      </ViewModeProvider>
    </AuthProvider>
    </TenantProvider>
  );
}

export default App;
